import {
	CVVInput,
	CreditCardNumberInput,
	EmotionBreakpoint,
	ExpirationDateInput,
	GetCardInfo,
	Checkbox,
	RadioButton,
	useBrandDetails,
} from 'dw-au-react-components';
import ReCAPTCHA from 'react-google-recaptcha';
import React, { useEffect, useState, useContext, useRef } from 'react';

import { CreditCardIconGroup } from '../../CreditCardIconGroup';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import useForm from 'react-hook-form';
import CartHeader from '../../ShoppingCart/CartHeader';
import PayPalLogo from '../../../svg/PayPalLogo';
import { VoucherCodeForm } from '../VoucherCodeForm';
import { VoucherAccordian } from '../../VoucherAccordian';

const StyledPaymentForm = styled.form`
	width: 100%;
	.form-row {
		margin-bottom: 25px;
	}
	.button-wrapper {
		display: flex;
		justify-content: center;
		align-items: center;
		button {
			width: 328px;
			transition: all 0.3s ease-in-out 0s !important;
		}
	}
	.card-expiration-cvv {
		display: flex;
		justify-content: space-between;
		${EmotionBreakpoint('mobile')} {
			flex-direction: column;
		}
		> div {
			flex: 1;
			width: 100%;
		}
		> div:last-of-type {
			padding-left: 15px;
			${EmotionBreakpoint('mobile')} {
				padding-left: 0px;
			}
		}
	}
	.backtopayements__btn {
		padding: 0;
		height: 25px;
		label {
			cursor: pointer;
		}
	}
`;

const StyledPaymentLayout = styled.div`
	.paymentType__info {
		letter-spacing: 1px;
		color: #555555;
	}
	.payment__notice {
		background: #c7d5c7;
		border-radius: 8px;
	}

	#receiveEmail,
	#receiveEmail2 {
		width: 5rem;
	}
`;

const PaymentForm = ({
	handleFormSubmission,
	defaultValues,
	step,
	setStep,
	backToStep,
	isLoggedIn,
	message = '',
	enablePaypal,
	allowExistingCustomer,
	showVouchers,
	setReceiveEmail,
	receiveEmail,
}) => {
	const { register, errors, handleSubmit, watch, reset, formState } = useForm({
		mode: 'onChange',
		defaultValues,
	});
	const isHideCaptcha =
		window.location.hostname.includes('uat.') ||
		window.location.hostname.includes('st.') ||
		window.location.hostname.includes('sun-webdev') ||
		window.location.hostname.includes('rebrand.');
	const { name, googleReCaptchaSiteKey } = useBrandDetails();
	const validCards = ['MasterCard', 'Visa', 'AmericanExpress'];
	const watchedCreditCardNumber = watch('creditCardNumber');
	const { cardType, cvvLength } = GetCardInfo(watchedCreditCardNumber);
	const activeCard = validCards.indexOf(cardType) >= 0 ? cardType : 'All';
	const [captchaVerified, setCaptchaVerified] = useState(
		isHideCaptcha ? true : false
	);
	const [isCaptchaLoaded, setIsCaptchaLoaded] = useState(
		isHideCaptcha ? true : false
	);
	const [captchaError, setCaptchaError] = useState(false);
	const recaptchaRef = useRef();
	const onSubmitPayment = (data) => {
		setCaptchaError(false);
		let expirationDate = data.expirationDate.split('/');

		data.cardType = cardType;
		data.expirationMonth = expirationDate[0];
		data.expirationYear = `20${expirationDate[1]}`;

		if ((isCaptchaLoaded && captchaVerified) || isLoggedIn) {
			handleFormSubmission(data);
		} else {
			setCaptchaError(true);
		}
	};

	const [addCardToProfile, setAddCardToProfile] = useState(false);

	const toggleAddCard = () => {
		setAddCardToProfile(!addCardToProfile);
	};

	const [radioSelection, setRadioSelection] = useState('card');

	const onRadioChange = (e) => {
		setRadioSelection(e.target.value);
		setReceiveEmail(false);
		recaptchaRef.current.reset();
		setCaptchaVerified(false);
		setCaptchaError(false);
	};

	useEffect(() => {
		setReceiveEmail(false);
	}, []);

	useEffect(() => {
		if (defaultValues && defaultValues === 'paypal') {
			setRadioSelection('paypal');
		}
	}, [defaultValues]);

	return (
		<StyledPaymentLayout>
			<CartHeader title='Checkout' step={step} setStep={setStep} />

			<div className='subtitle mb-20 lower roboto'>Add Payment Method</div>
			{!isLoggedIn && message && (
				<div className={`mb-20 alert ${message?.class}`}>{message.message}</div>
			)}
			{/* {enablePaypal && !isLoggedIn && (
				<div className='text-small text-uppercase mb-15 font-helvetica paymentType__info'>
					Pay with Credit Card
				</div>
			)} */}

			<div className='form-row d-flex mb-25'>
				{enablePaypal && !isLoggedIn && (
					<RadioButton
						cypressClass='card-radio'
						value='card'
						isChecked={radioSelection === 'card' ? true : false}
						onChange={onRadioChange}
						className='mb-0'
					/>
				)}
				<CreditCardIconGroup activeCard={activeCard} validCards={validCards} />
			</div>

			{enablePaypal && !isLoggedIn && (
				<div className='form-row d-flex mb-40 dmlp_guest_paypal'>
					<RadioButton
						cypressClass='paypal-radio'
						value='paypal'
						isChecked={radioSelection === 'paypal' ? true : false}
						onChange={onRadioChange}
					/>
					<PayPalLogo />
				</div>
			)}

			{radioSelection !== 'paypal' && (
				<StyledPaymentForm
					className='payment-form'
					onSubmit={handleSubmit(onSubmitPayment)}
					data-cy='credit-card-form'
				>
					<div className='form-row'>
						<CreditCardNumberInput register={register} errors={errors} />
					</div>
					<div className='card-expiration-cvv'>
						<div className='form-row'>
							<ExpirationDateInput register={register} errors={errors} />
						</div>
						<div className='form-row'>
							<CVVInput
								register={register}
								errors={errors}
								maxLength={cvvLength}
							/>
						</div>
					</div>

					<div className='form-row'>
						<Checkbox
							onChange={toggleAddCard}
							label={
								isLoggedIn
									? 'Add this card to my wallet'
									: 'Save this payment method for future'
							}
							name='addcardtoprofile'
							register={register}
							checked={addCardToProfile}
						/>
					</div>

					{!isLoggedIn && showVouchers && (
						<VoucherAccordian title='Voucher Code'>
							<VoucherCodeForm />
						</VoucherAccordian>
					)}

					{!isLoggedIn && (
						<div className='form-row'>
							<div className='d-flex justify-content-start align-items-start'>
								<Checkbox
									label=''
									name='recieveEmail'
									id='receiveEmail'
									register={register({
										required:
											'Please tick the above box to proceed with your order today.',
									})}
									checked={receiveEmail}
									onChange={() => setReceiveEmail(!receiveEmail)}
								/>
								<label htmlFor='receiveEmail' className='pl-10'>
									By continuing, you confirm you are over 18 years of age and
									agree to the{' '}
									<a href='/terms' target='_blank'>
										Terms and Conditions
									</a>{' '}
									and have read the{' '}
									<a href='/privacy' target='_blank'>
										Privacy Policy
									</a>
									. You can manage your marketing preferences via your {name}{' '}
									account.
								</label>
							</div>
							{errors.recieveEmail ? (
								<div className='text-red font-weight-light'>
									{errors?.recieveEmail?.message}
								</div>
							) : null}
						</div>
					)}

					{!isLoggedIn && (
						<>
							<div className='checkout-verification form-row'>
								<ReCAPTCHA
									ref={recaptchaRef}
									sitekey={googleReCaptchaSiteKey}
									onChange={(value) => {
										setCaptchaVerified(value ? true : false);
									}}
									asyncScriptOnLoad={() => {
										setIsCaptchaLoaded(true);
									}}
								/>
							</div>
							{captchaError && !captchaVerified && isCaptchaLoaded ? (
								<div className='text-red font-weight-light mt-10 mb-20'>
									Please confirm you are not a robot by clicking the above
									checkbox.
								</div>
							) : null}
						</>
					)}

					<div className='form-row'>
						<button
							// disabled={!formState.isValid || !formState.dirty}
							// disabled={
							// 	isLoggedIn && allowExistingCustomer ? false : !receiveEmail
							// }
							type='submit'
							className='btn btn-primary font-weight-bold dmlp__btn w-100 mb-20 dmlp_guest_place_order'
						>
							{isLoggedIn && allowExistingCustomer
								? 'Add Card'
								: 'Confirm & Place Order'}
						</button>
						<div
							className='btn btn-outline-secondary btn-block'
							onClick={() => {
								reset({});
								if (backToStep) {
									setStep(backToStep);
								} else {
									setStep(6);
								}
							}}
						>
							Go back to{' '}
							{isLoggedIn && allowExistingCustomer ? 'payment' : 'shipping'}{' '}
							details
						</div>
					</div>
				</StyledPaymentForm>
			)}

			{enablePaypal && !isLoggedIn && radioSelection === 'paypal' && (
				<>
					{/* <div className='text-small text-uppercase mb-15 font-helvetica paymentType__info'>
						Pay with Paypal
					</div> */}

					<div className='pt-15 pb-15 pr-20 pl-20 payment__notice mb-20'>
						<strong>PLEASE NOTE:</strong> You will be taken to PayPal’s site to
						complete your transaction once you confirm your order on the
						confirmation screen.
					</div>
					<div className='form-row mb-20'>
						<div className='d-flex justify-content-start align-items-start'>
							<Checkbox
								label=''
								name='recieveEmail'
								id='receiveEmail2'
								register={register}
								checked={receiveEmail}
								onChange={() => setReceiveEmail(!receiveEmail)}
							/>
							<label htmlFor='receiveEmail2' className='pl-10'>
								By continuing, you confirm you are over 18 years of age and
								agree to the{' '}
								<a href='/terms' target='_blank'>
									Terms and Conditions
								</a>{' '}
								and have read the{' '}
								<a href='/privacy' target='_blank'>
									Privacy Policy
								</a>
								. You can manage your marketing preferences via your {name}{' '}
								account.
							</label>
						</div>
						{!receiveEmail ? (
							<div className='text-red font-weight-light'>
								Please tick the above box to proceed with your order today.
							</div>
						) : null}
					</div>

					<div className='checkout-verification form-row mb-20'>
						<ReCAPTCHA
							ref={recaptchaRef}
							sitekey={googleReCaptchaSiteKey}
							onChange={(value) => {
								setCaptchaVerified(value ? true : false);
							}}
							asyncScriptOnLoad={() => {
								setIsCaptchaLoaded(true);
							}}
						/>
					</div>

					{!captchaVerified && isCaptchaLoaded ? (
						<div className='text-red font-weight-light mb-20'>
							Please confirm you are not a robot by clicking the above checkbox.
						</div>
					) : null}

					<div className='form-row'>
						<button
							type='button'
							disabled={!receiveEmail || !captchaVerified}
							className='btn btn-paypal dmlp__btn w-100 mb-20 dmlp_guest_place_order d-flex justify-content-center align-items-center'
							title='Pay with PayPal'
							onClick={() =>
								receiveEmail &&
								captchaVerified &&
								handleFormSubmission('paypal')
							}
						>
							Pay with <PayPalLogo className='ml-12' />
						</button>
						<div
							className='btn btn-outline-secondary btn-block'
							onClick={() => {
								reset({});
								if (backToStep) {
									setStep(backToStep);
								} else {
									setStep(6);
								}
							}}
						>
							Go back to shipping details
						</div>
					</div>
				</>
			)}
		</StyledPaymentLayout>
	);
};

PaymentForm.defaultProps = {
	handleFormSubmission: () => {},
	defaultValues: {
		creditCardNumber: '',
		expirationDate: '',
		cvv: '',
		addcardtoprofile: false,
	},
	visitorType: '',
	paymentWallet: '',
	handleOtherPayments: () => {},
};
PaymentForm.propTypes = {
	/** Function that handles form submission */
	handleFormSubmission: PropTypes.func.isRequired,
	/**
	 * the default values to display in the form
	 */
	defaultValues: PropTypes.any,
};
export default PaymentForm;
